import SideBarMenuBlock from './SideBarMenuBlock/SideBarMenuBlock'
import { NavLink } from 'react-router-dom'
import { useLayoutDispatch, useLayoutState } from '@context/layout/context/layout.context'
import { useAuthState } from '@context/auth/context/auth.context'
import { useUserState } from '@context/user/context/user.context'
import { hasPermissions, filterDevicesDueTo30DaysReport } from '@common/utils/helperFunctions'
import { useDeviceState } from '@context/device/context/device.context'
import { ReactNode } from 'react'
import {
  FaHandsHelping,
  FaTachometerAlt,
  FaDollarSign,
  FaCloud,
  FaWater,
  FaHistory,
  FaFlask,
  FaFaucet,
  FaCog,
  FaMoneyCheck,
  FaBuilding
} from "react-icons/fa"
import { FaTriangleExclamation, FaUsers, FaRss } from "react-icons/fa6"


export interface IMenuItems {
  href: string
  title: string
  icon: ReactNode
  permissions: string[]
  defaultShow: boolean
  active?: boolean
  tab?: string
}

interface INavItems {
  heading: string
  menuItems: IMenuItems[]
}

function SideBar() {
  const { toggleSideBar } = useLayoutDispatch()
  const { shouldShowSideBar } = useLayoutState()
  const { permissions } = useAuthState()
  const { userInfo } = useUserState()
  const { devices } = useDeviceState()

  const devicesDueTo30DaysReport = filterDevicesDueTo30DaysReport(devices)

  const menu: INavItems[] = [
    {
      heading: 'Dashboard',
      menuItems: [
        {
          href: '/home',
          title: 'Dashboard',
          icon: <FaTachometerAlt />,
          permissions: [],
          defaultShow: false,
        },
        {
          href: '/hourly-usage',
          title: 'Hourly Usage',
          icon: <FaTachometerAlt />,
          permissions: [],
          defaultShow: false,
        },
        {
          href: '/device-status',
          title: 'Device Status',
          icon: <FaRss />,
          permissions: ['READ:DEVICE:PROD', 'READ:DEVICE:STATUS:PROD'],
          defaultShow: false,
        },
        {
          href: '/total-spent',
          title: 'Total Spent',
          icon: <FaDollarSign />,
          permissions: ['READ:USAGE:COST'],
          defaultShow: false,
        },
        {
          href: '/device-co2',
          title: 'Total Co2 Produced',
          icon: <FaCloud />,
          permissions: ['READ:REPORTS:CO2'],
          defaultShow: false,
        },
        {
          href: '/alert-queue',
          title: 'Alert Queue',
          icon: <FaTriangleExclamation />,
          permissions: ['READ:ALERTS:USAGE'],
          defaultShow: false,
        },
        {
          href: '/customer-journey',
          title: 'Customer Journey',
          icon: <FaUsers />,
          permissions: ['ACCOUNT:ADMIN:SMARTFLOW'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Reporting',
      menuItems: [
        {
          href: '/30days',
          title: '30 Days Report',
          icon: <FaWater />,
          permissions: ['READ:REPORTS:30DAY'],
          defaultShow: false,
        },
        {
          href: '/esg-report',
          title: `ESG Report ${
            hasPermissions(permissions, ['ACCOUNT:ADMIN:SMARTFLOW']) &&
            devicesDueTo30DaysReport.length > 0
              ? `(${devicesDueTo30DaysReport.length})`
              : ''
          }`,
          icon: <FaWater />,
          permissions: ['READ:REPORTS:ESG'],
          defaultShow: false,
        },
        {
          href: '/water-usage',
          title: 'Water Usage',
          icon: <FaWater />,
          permissions: ['READ:USAGE'],
          defaultShow: false,
        },
        {
          href: '/historical-alerts',
          title: 'Historical Alerts',
          icon: <FaTriangleExclamation />,
          permissions: ['READ:ALERTS:USAGE:HISTORY'],
          defaultShow: false,
        },
        {
          href: '/invoice',
          title: 'Invoice',
          icon: <FaMoneyCheck />,
          permissions: ['READ:USAGE:INVOICE'],
          defaultShow: false,
          active: !!userInfo.accounts.find((a) => a.invoice),
        },
      ],
    },
    {
      heading: 'Control',
      menuItems: [
        {
          href: '/real-time-flow',
          title: 'Real-Time Flow',
          icon: <FaWater />,
          permissions: ['READ:DEVICE:RTF'],
          defaultShow: false,
        },
        {
          href: '/valves-management',
          title: 'Operate Valves',
          icon: <FaFaucet />,
          permissions: ['DEVICE:VALVE:PROD:OPERATE'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Support Tasks',
      menuItems: [
        {
          href: '/account-management?tab=locations_and_devices',
          title: 'Account Management',
          icon: <FaBuilding />,
          permissions: ['UPDATE:ACCOUNT'],
          defaultShow: false,
        },
        {
          href: '/account-management?tab=users_and_invitations',
          title: 'User Management',
          icon: <FaUsers />,
          permissions: ['UPDATE:DEVICE:PROD:USER'],
          defaultShow: false,
        },
        // {
        //   href: '/user-management',
        //   title: 'User Management',
        //   icon: 'fa-user',
        //   permissions: ['UPDATE:DEVICE:PROD:USER'],
        //   defaultShow: false,
        // },
        {
          href: '/device-management',
          title: 'Device Management',
          icon: <FaCog />,
          permissions: ['READ:DEVICE:PROD'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Test Devices',
      menuItems: [
        {
          href: '/test-water-usage',
          title: 'Water Usage',
          icon: <FaWater />,
          permissions: ['READ:USAGE:TEST'],
          defaultShow: false,
        },
        {
          href: '/test-device-status',
          title: 'Device Status',
          icon: <FaRss />,
          permissions: ['READ:DEVICE:STATUS:TEST'],
          defaultShow: false,
        },
        {
          href: '/test-valves-management',
          title: 'Operate Valves',
          icon: <FaFaucet />,
          permissions: ['DEVICE:VALVE:TEST:OPERATE'],
          defaultShow: false,
        },
        {
          href: '/test-bench',
          title: 'Test Bench',
          icon: <FaFlask />,
          permissions: ['READ:DEVICE:TEST', 'READ:DEVICE:STGD'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Utilities',
      menuItems: [
        {
          href: '/device-logger',
          title: 'Logger',
          icon: <FaHistory />,
          permissions: ['devices:logger:view'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Help',
      menuItems: [
        {
          href: '/tutorials',
          title: 'Tutorials',
          icon: <FaHandsHelping />,
          permissions: [],
          defaultShow: false,
        },
      ],
    },
  ]

  return (
    <ul
      style={{ overflowY: 'auto', scrollbarWidth: 'thin', height: '100vh' }}
      className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${
        shouldShowSideBar ? 'toggled' : ''
      }`}
      id="accordionSidebar"
    >
      {/*<ul style={{overflowY:'auto', scrollbarWidth: 'thin', height: '100vh'}} className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${(screenType === 'mobile' || screenType === 'tablet' || screenType === 'laptop') && shouldShowSideBar ? 'toggled' : '' }`} id="accordionSidebar">*/}
      <div style={{ display: 'flex' }}>
        <NavLink
          style={{ margin: 'auto' }}
          onClick={toggleSideBar}
          className="sidebar-brand d-flex align-items-center justify-content-center"
          to="/home"
        >
          <div className="sidebar-brand-icon">
            <img
              alt="Smartflow"
              style={{ width: '100%', height: '30px' }}
              src="/assets/img/logo.svg"
            />
          </div>
        </NavLink>
        {/*{(screenType === 'mobile' || screenType === 'tablet' || screenType === 'laptop') ? <div style={{margin: 'auto'}}><i style={{color: 'white'}} onClick={toggleSideBar} className="fas fa-times"></i></div> : null}*/}
        <div style={{ margin: 'auto', cursor: 'pointer' }}>
          <i style={{ color: 'white' }} onClick={toggleSideBar} className="fas fa-times "></i>
        </div>
      </div>
      <div>
      <hr className="sidebar-divider" />
      <li
        className="nav-item"
        style={{backgroundColor: 'forestgreen'}}
        key={'static-item-1'}
      >
        <NavLink
          className={({ isActive }) => 'nav-link pb-1' + (isActive ? ' active' : '')}
          to={'/tutorials'}
          onClick={toggleSideBar}
        >
          <FaHandsHelping />
          <span style={{ marginLeft: '1em' }}>{"Explore How Videos"}</span>
        </NavLink>

      </li>
      </div>
      {menu.map((menuCategory) => {
        const filteredItems = menuCategory.menuItems.filter(
          (mi: { permissions: string[]; defaultShow: boolean }) => {
            // if (mi.permissions.length > 0)
            //    return mi.permissions.filter(p => permissions.findIndex(pp => pp.name === p) >= 0).length > 0
            // return true;
            return mi.permissions.length > 0
              ? hasPermissions(permissions, mi.permissions) || mi.defaultShow
              : true
          },
        )
        return filteredItems.length > 0 ? (
          <SideBarMenuBlock
            key={menuCategory.heading}
            heading={menuCategory.heading}
            menuItems={filteredItems}
          />
        ) : null
      })}
    </ul>
  )
}
export default SideBar
